import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { adminDownloadBookingsCsv, adminGetBooking, adminListBookings, adminUpdateBooking } from '../../api/adminBookings';
import { amendmentTypeLabel } from '../../api/amendments';
import AdminShell, { useAdminAccess } from './AdminShell';
import {
  ProfitTiles, bookingStatuses, describeStatus, fmtDateTime, fmtDay, margin, money, productLabel, productTypes, statusClass,
} from './bookingFormat';

const filterKeys = ['product', 'status', 'from', 'to', 'search'];

/** Every customer's bookings with supplier price, sale price and profit. Filters live in the URL so views can be shared. */
export default function AdminBookingsPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [params, setParams] = useSearchParams();
  const filters = Object.fromEntries(filterKeys.map((k) => [k, params.get(k) || '']));
  const page = Number(params.get('page')) || 1;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState(filters.search);
  const [openId, setOpenId] = useState(params.get('booking') || null);
  const [exporting, setExporting] = useState(false);
  const filterKey = params.toString();

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await adminListBookings(auth.accessToken, { ...Object.fromEntries(filterKeys.map((k) => [k, params.get(k)])), page: Number(params.get('page')) || 1, pageSize: 25 }));
    } catch (requestError) {
      setError(requestError.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.accessToken, filterKey]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    setParams(next);
  };
  const goToPage = (n) => { const next = new URLSearchParams(params); next.set('page', n); setParams(next); };

  const exportCsv = async () => {
    setExporting(true);
    try { await adminDownloadBookingsCsv(auth.accessToken, filters); } catch (e) { setError(e.message); } finally { setExporting(false); }
  };

  return (
    <AdminShell
      title="All bookings"
      subtitle="Every customer booking across products, with what the supplier charged, what the customer paid and what we kept."
      actions={<button type="button" className="btn btn-outline btn-sm" onClick={exportCsv} disabled={exporting}>{exporting ? 'Exporting…' : 'Export CSV'}</button>}
    >
      <form className="admin-filters" onSubmit={(e) => { e.preventDefault(); setFilter('search', searchText.trim()); }}>
        <label className="field">
          <span>Product</span>
          <select value={filters.product} onChange={(e) => setFilter('product', e.target.value)}>
            <option value="">All products</option>
            {productTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
            <option value="">Any status</option>
            {bookingStatuses.map((s) => <option key={s} value={s}>{describeStatus(s)}</option>)}
          </select>
        </label>
        <label className="field"><span>Booked from</span><input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => setFilter('from', e.target.value)} /></label>
        <label className="field"><span>Booked to</span><input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilter('to', e.target.value)} /></label>
        <label className="field grow">
          <span>Search</span>
          <input type="search" placeholder="Reference, customer, email, phone or route" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </label>
        <button type="submit" className="btn btn-primary btn-sm">Search</button>
        {filterKeys.some((k) => filters[k]) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSearchText(''); setParams(new URLSearchParams()); }}>Clear</button>
        )}
      </form>

      {error && <p className="api-message error" role="alert">{error}</p>}
      {!data && !error && <p role="status">Loading bookings…</p>}
      {data && (
        <>
          <ProfitTiles totals={data.totals} />
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Booked</th><th>Product</th><th>Reference</th><th>Trip</th><th>Customer</th><th>Status</th>
                  <th className="num">Supplier price</th><th className="num">Sale price</th><th className="num">Discount</th><th className="num">Profit</th><th />
                </tr>
              </thead>
              <tbody>
                {data.bookings.map((b) => (
                  <tr key={b.id} className="admin-click-row" onClick={() => setOpenId(b.id)}>
                    <td>{fmtDateTime(b.createdAt)}</td>
                    <td>{productLabel[b.productType] || b.productType}<br /><span className="muted small">{b.supplierCode}</span></td>
                    <td>{b.supplierBookingReference || '—'}</td>
                    <td className="admin-wrap">{b.summary || '—'}{b.travelDate && <><br /><span className="muted small">{fmtDay(b.travelDate)}</span></>}</td>
                    <td>{b.customerName}<br /><span className="muted small">{b.customerEmail}</span></td>
                    <td><span className={statusClass(b.status)}>{describeStatus(b.status)}</span></td>
                    <td className="num">{money(b.supplierAmount)}</td>
                    <td className="num">{money(b.saleAmount)}</td>
                    <td className="num">{b.discountAmount ? money(b.discountAmount) : '—'}</td>
                    <td className={`num ${b.profit < 0 ? 'loss' : 'gain'}`}><b>{money(b.profit)}</b></td>
                    <td>{b.openAmendments > 0 && <span className="status-pill" title="Open change requests">{b.openAmendments} request{b.openAmendments > 1 ? 's' : ''}</span>}</td>
                  </tr>
                ))}
                {!data.bookings.length && <tr><td colSpan="11">No bookings match these filters.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination">
            <span>{data.totalCount} bookings</span>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>Previous</button>
            <span>Page {page}</span>
            <button className="btn btn-outline btn-sm" disabled={page * data.pageSize >= data.totalCount} onClick={() => goToPage(page + 1)}>Next</button>
          </div>
        </>
      )}

      {openId && <BookingDetail id={openId} token={auth.accessToken} onClose={() => setOpenId(null)} onSaved={load} />}
    </AdminShell>
  );
}

function BookingDetail({ id, token, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    adminGetBooking(token, id)
      .then((d) => { if (active) { setDetail(d); setForm(toForm(d.booking)); } })
      .catch((e) => active && setError(e.message));
    return () => { active = false; };
  }, [token, id]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setError(''); setMessage('');
    try {
      const saved = await adminUpdateBooking(token, id, {
        status: form.status,
        customerRefund: Number(form.customerRefund) || 0,
        supplierRefund: Number(form.supplierRefund) || 0,
      });
      setDetail(saved); setForm(toForm(saved.booking));
      setMessage('Booking updated. This changes our records only; it does not contact the supplier or refund the payment.');
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const b = detail?.booking;
  return (
    <div className="overlay" onClick={onClose}>
      <div className="overlay-card modal admin-detail" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Booking details">
        <div className="admin-detail-head">
          <h3>{b ? `${productLabel[b.productType] || b.productType} · ${b.supplierBookingReference || b.id}` : 'Booking'}</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {error && <p className="api-message error" role="alert">{error}</p>}
        {!detail && !error && <p role="status">Loading…</p>}
        {b && (
          <>
            <div className="kv-grid">
              <div><span className="muted small">Status</span><b><span className={statusClass(b.status)}>{describeStatus(b.status)}</span></b></div>
              <div><span className="muted small">Booked</span><b>{fmtDateTime(b.createdAt)}</b></div>
              <div><span className="muted small">Trip</span><b>{b.summary || '—'}</b></div>
              <div><span className="muted small">Travel date</span><b>{fmtDay(b.travelDate)}</b></div>
              <div><span className="muted small">Customer</span><b>{b.customerName}</b></div>
              <div><span className="muted small">Lead traveller</span><b>{b.leadTraveller || '—'}{b.travellers > 1 ? ` (+${b.travellers - 1})` : ''}</b></div>
              <div><span className="muted small">Email</span><b><a className="link" href={`mailto:${b.customerEmail}`}>{b.customerEmail}</a></b></div>
              <div><span className="muted small">Phone</span><b>{b.customerPhone ? <a href={`tel:${b.customerPhone}`}>{b.customerPhone}</a> : '—'}</b></div>
              <div><span className="muted small">Supplier</span><b>{b.supplierCode}</b></div>
              <div><span className="muted small">Booking ID</span><b className="small">{b.id}</b></div>
            </div>

            <h4>Money</h4>
            <table className="admin-table compact">
              <tbody>
                <tr><td>Supplier price (paid to {b.supplierCode})</td><td className="num">{money(b.supplierAmount)}</td></tr>
                <tr><td>+ Markup</td><td className="num">{money(b.markup)}</td></tr>
                <tr><td>+ Transaction fee (to payment gateway)</td><td className="num">{money(b.transactionFee)}</td></tr>
                <tr><td>− Discount{b.promoCode ? ` (${b.promoCode})` : ''}</td><td className="num">{money(-b.discountAmount)}</td></tr>
                <tr><td><b>Sale price (customer paid)</b></td><td className="num"><b>{money(b.saleAmount)}</b></td></tr>
                {(b.customerRefund > 0 || b.supplierRefund > 0) && <>
                  <tr><td>− Refunded to customer</td><td className="num">{money(-b.customerRefund)}</td></tr>
                  <tr><td>+ Refunded by supplier</td><td className="num">{money(b.supplierRefund)}</td></tr>
                </>}
                <tr><td><b>Profit</b> <span className="muted small">({margin(b.profit, b.saleAmount)} of sale)</span></td><td className={`num ${b.profit < 0 ? 'loss' : 'gain'}`}><b>{money(b.profit)}</b></td></tr>
              </tbody>
            </table>

            <h4>Payments</h4>
            {detail.payments.length ? (
              <table className="admin-table compact">
                <thead><tr><th>Gateway</th><th>Method</th><th>Status</th><th className="num">Amount</th><th>Payment / refund ID</th></tr></thead>
                <tbody>
                  {detail.payments.map((p) => (
                    <tr key={p.id}>
                      <td>{p.gateway}</td><td>{p.method}</td>
                      <td><span className={statusClass(p.status === 'Completed' ? 'Confirmed' : p.status)}>{p.status}</span></td>
                      <td className="num">{money(p.amount)}</td>
                      <td className="small">{p.gatewayPaymentId || p.gatewayOrderId || '—'}{p.gatewayRefundId && <><br />Refund {p.gatewayRefundId}</>}{p.failureReason && <><br /><span className="muted">{p.failureReason}</span></>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="muted small">No gateway payment is linked to this booking.</p>}

            <h4>Change requests</h4>
            {detail.amendments.length ? (
              <ul className="admin-amend-list">
                {detail.amendments.map((a) => (
                  <li key={a.id}>
                    <span className={statusClass(a.status)}>{describeStatus(a.status)}</span>{' '}
                    <b>{amendmentTypeLabel[a.type] || a.type}</b> · {fmtDateTime(a.createdAt)} — {a.message}{' '}
                    <Link className="link small" to={`/admin/amendments?id=${a.id}`}>Open</Link>
                  </li>
                ))}
              </ul>
            ) : <p className="muted small">None.</p>}

            <h4>Correct booking</h4>
            <p className="muted small">Use after a cancellation or change done with the supplier outside the website, so reports stay right.</p>
            {message && <p className="api-message success" role="status">{message}</p>}
            <form className="admin-filters" onSubmit={save}>
              <label className="field"><span>Status</span>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {bookingStatuses.map((s) => <option key={s} value={s}>{describeStatus(s)}</option>)}
                </select>
              </label>
              <label className="field"><span>Refunded to customer (₹)</span>
                <input type="number" min="0" max={b.saleAmount} step="any" value={form.customerRefund} onChange={(e) => setForm({ ...form, customerRefund: e.target.value })} />
              </label>
              <label className="field"><span>Refunded by supplier (₹)</span>
                <input type="number" min="0" max={b.supplierAmount} step="any" value={form.supplierRefund} onChange={(e) => setForm({ ...form, supplierRefund: e.target.value })} />
              </label>
              <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const toForm = (b) => ({ status: b.status, customerRefund: String(b.customerRefund), supplierRefund: String(b.supplierRefund) });
